import { PageHeader } from "../../../bits/PageHeader/PageHeader";
import * as React from "react";
import { ConsentForm } from "intake_shared/src/types";
import { Button } from "../../../bits/Button/Button";
import styles from "./ConsentForms.module.scss";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { useToasts } from "react-toast-notifications";
import {
    addConsentFormType,
    updateConsentCache,
    updateConsentFormLink,
} from "../../../../../store/consent_form";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import { Input } from "../../../bits/Input/Input";
import EditIcon from "../../../icons/Edit";
import CancelIcon from "../../../icons/Cancel";
import CheckIcon from "../../../icons/Check";
import AddConsentFormDialog from "../AddConsentFormDialog/AddConsentFormDialog";
import { v4 as uuidv4 } from "uuid";

interface AddTypeState {
    consentFormName: string;
    typeName: string;
    typeLink: string;
}

export default function ConsentForms() {
    const olc = React.useContext(CacheContext);
    const { addToast } = useToasts();
    const allAvailableConsentForms = olc?.consentForms || [];
    const [editingLinkId, setEditingLinkId] = React.useState<string | null>(null);
    const [newLink, setNewLink] = React.useState("");
    const [selectedConsentForm, setSelectedConsentForm] =
        React.useState<ConsentForm | null>(null);
    const [consentForms, setConsentForms] = React.useState<ConsentForm[]>([]);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [addingTypeTo, setAddingTypeTo] = React.useState<AddTypeState | null>(
        null
    );

    const handleSave = async () => {
        if (!editingLinkId) return;
        await updateConsentFormLink(
            selectedConsentForm?.name || "",
            editingLinkId,
            newLink
        );
        const returnedConsentForms = await updateConsentCache();
        if (returnedConsentForms) {
            olc?.updateConsentForms(returnedConsentForms);
        }
        addToast("Consent link updated successfully", {
            appearance: "success",
            autoDismiss: true,
        });
        setEditingLinkId(null);
    };

    const handleAddConsentForm = (newForm: ConsentForm) => {
        setConsentForms([...consentForms, newForm]);
        addToast(`Consent Form Added`, {
            appearance: "success",
            autoDismiss: true,
        });
        setIsDialogOpen(false);
    };

    const handleSaveNewType = async () => {
        if (!addingTypeTo || !addingTypeTo.typeLink.trim()) return;

        await addConsentFormType(addingTypeTo.consentFormName, {
            id: uuidv4(),
            name: addingTypeTo.typeName,
            link: addingTypeTo.typeLink,
        });

        const returnedConsentForms = await updateConsentCache();
        if (returnedConsentForms) {
            olc?.updateConsentForms(returnedConsentForms);
        }

        addToast("Consent type added successfully", {
            appearance: "success",
            autoDismiss: true,
        });

        setAddingTypeTo(null);
    };

    return (
        <div className={styles.consentForms}>
            <PageHeader
                title="Consent Form Links"
                actions={
                    <Button onClick={() => setIsDialogOpen(true)}>
                        Add consent form
                    </Button>
                }
                className={styles.pageHeader}
            />
            <div className={styles.content}>
                {Object.entries(allAvailableConsentForms).map(
                    ([consent, consentDetails], idx) => {
                        const isAddingTypeHere =
                            addingTypeTo?.consentFormName === consentDetails.name;

                        return (
                            <div className={styles.consentFormCard} key={idx}>
                                <div className={styles.consentFormCardHeader}>
                                    <p className={styles.consentFormName}>
                                        {consentDetails.name}
                                    </p>
                                    <Button
                                        variant="secondary"
                                        className={styles.addTypeButton}
                                        onClick={() => {
                                            if (isAddingTypeHere) {
                                                setAddingTypeTo(null);
                                            } else {
                                                setAddingTypeTo({
                                                    consentFormName:
                                                        consentDetails.name,
                                                    typeName: "",
                                                    typeLink: "",
                                                });
                                            }
                                        }}
                                    >
                                        {isAddingTypeHere ? "Cancel" : "+ Add type"}
                                    </Button>
                                </div>

                                <div className={styles.consentFormTypes}>
                                    {consentDetails.types.map((type) => (
                                        <div
                                            className={styles.consentFormTypeWrapper}
                                            key={type.id}
                                        >
                                            <div className={styles.consentFormType}>
                                                {type.name ? (
                                                    <span>For {type.name}:</span>
                                                ) : null}
                                                {editingLinkId === type.id ? (
                                                    <Input
                                                        type="url"
                                                        value={newLink}
                                                        onChange={(e) =>
                                                            setNewLink(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                ) : (
                                                    <a
                                                        href={type.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-muted-active"
                                                    >
                                                        {type.link}
                                                    </a>
                                                )}
                                            </div>
                                            {editingLinkId !== type.id ? (
                                                <Button
                                                    variant="normal"
                                                    onClick={() => {
                                                        setSelectedConsentForm(
                                                            consentDetails
                                                        );
                                                        setNewLink(type.link);
                                                        setEditingLinkId(type.id);
                                                    }}
                                                >
                                                    <EditIcon stroke="var(--color-gray)" />
                                                </Button>
                                            ) : (
                                                <div
                                                    className={
                                                        styles.consentFormTypeButtons
                                                    }
                                                >
                                                    <Button
                                                        variant="primary"
                                                        size="small"
                                                        iconRight={<CheckIcon />}
                                                        onClick={handleSave}
                                                        disabled={!newLink.trim()}
                                                    >
                                                        Confirm
                                                    </Button>
                                                    <Button
                                                        variant="secondary"
                                                        size="small"
                                                        onClick={() =>
                                                            setEditingLinkId(null)
                                                        }
                                                        iconLeft={
                                                            <CancelIcon stroke="currentColor" />
                                                        }
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {isAddingTypeHere && addingTypeTo && (
                                        <div className={styles.addTypeForm}>
                                            <div className={styles.addTypeInputs}>
                                                <Input
                                                    label="Type Name"
                                                    placeholder="e.g. Adults, Minors, Minors - Secondary Custodian"
                                                    value={addingTypeTo.typeName}
                                                    onChange={(e) =>
                                                        setAddingTypeTo({
                                                            ...addingTypeTo,
                                                            typeName: e.target.value,
                                                        })
                                                    }
                                                />
                                                <Input
                                                    label="Link"
                                                    type="url"
                                                    isRequired
                                                    value={addingTypeTo.typeLink}
                                                    onChange={(e) =>
                                                        setAddingTypeTo({
                                                            ...addingTypeTo,
                                                            typeLink: e.target.value,
                                                        })
                                                    }
                                                />
                                            </div>
                                            <div className={styles.addTypeActions}>
                                                <Button
                                                    variant="primary"
                                                    size="small"
                                                    iconRight={<CheckIcon />}
                                                    onClick={handleSaveNewType}
                                                    disabled={
                                                        !addingTypeTo.typeLink.trim()
                                                    }
                                                >
                                                    Save type
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="small"
                                                    onClick={() =>
                                                        setAddingTypeTo(null)
                                                    }
                                                    iconLeft={
                                                        <CancelIcon stroke="currentColor" />
                                                    }
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }
                )}
                {Object.keys(allAvailableConsentForms).length === 0 && (
                    <EmptyListState
                        emptyMessage="No consent forms added yet"
                        stateHelperText="Click 'Add consent form' to create one."
                    />
                )}
            </div>
            <AddConsentFormDialog
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onSave={handleAddConsentForm}
            />
        </div>
    );
}
